import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase, TIERS, TIER_LIMITS } from '../lib/supabase'

export const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null,
      session: null,
      isLoading: true,
      isAuthenticated: false,
      tier: TIERS.FREE,
      subscription: null,
      tierLimits: TIER_LIMITS[TIERS.FREE],
      emailPreferences: {
        arbAlerts: true,
        dailyDigest: true,
        weeklyReport: true,
        promotions: false,
      },

      // Initialize
      initialize: async () => {
        if (!supabase) {
          set({ isLoading: false })
          return
        }

        try {
          const { data: { session } } = await supabase.auth.getSession()

          if (session?.user) {
            // Try to fetch profile - may not exist yet for new users
            let profile = null
            try {
              const { data, error } = await supabase
                .from('profiles')
                .select('*, subscriptions(*)')
                .eq('id', session.user.id)
                .single()

              // Only use profile if fetch succeeded (table exists and row found)
              if (!error) {
                profile = data
              }
            } catch {
              // Profiles table may not exist yet - use defaults silently
            }

            const tier = profile?.subscriptions?.tier || TIERS.FREE

            set({
              user: session.user,
              session,
              isAuthenticated: true,
              tier,
              subscription: profile?.subscriptions,
              tierLimits: TIER_LIMITS[tier],
              emailPreferences: profile?.email_preferences || get().emailPreferences,
              isLoading: false,
            })
          } else {
            set({ isLoading: false })
          }

          // Listen for auth changes
          supabase.auth.onAuthStateChange(async (event, session) => {
            if (event === 'SIGNED_IN' && session?.user) {
              // Try to fetch profile - may not exist yet
              let profile = null
              try {
                const { data, error } = await supabase
                  .from('profiles')
                  .select('*, subscriptions(*)')
                  .eq('id', session.user.id)
                  .single()

                if (!error) {
                  profile = data
                }
              } catch {
                // Profiles table may not exist - use defaults
              }

              const tier = profile?.subscriptions?.tier || TIERS.FREE

              set({
                user: session.user,
                session,
                isAuthenticated: true,
                tier,
                subscription: profile?.subscriptions,
                tierLimits: TIER_LIMITS[tier],
              })
            } else if (event === 'SIGNED_OUT') {
              set({
                user: null,
                session: null,
                isAuthenticated: false,
                tier: TIERS.FREE,
                subscription: null,
                tierLimits: TIER_LIMITS[TIERS.FREE],
              })
            }
          })
        } catch (error) {
          console.error('Auth initialization error:', error)
          set({ isLoading: false })
        }
      },

      // Sign up
      signUp: async (email, password, metadata = {}) => {
        if (!supabase) throw new Error('Auth not configured')
        set({ isLoading: true })

        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: metadata,
            emailRedirectTo: `${window.location.origin}/auth/callback`
          }
        })

        set({ isLoading: false })
        if (error) throw error
        return data
      },

      // Sign in
      signIn: async (email, password) => {
        if (!supabase) throw new Error('Auth not configured')
        set({ isLoading: true })

        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password
        })

        set({ isLoading: false })
        if (error) throw error
        return data
      },

      // OAuth (Google, Discord)
      signInWithOAuth: async (provider) => {
        if (!supabase) throw new Error('Auth not configured')

        const { data, error } = await supabase.auth.signInWithOAuth({
          provider,
          options: {
            redirectTo: `${window.location.origin}/auth/callback`
          }
        })

        if (error) throw error
        return data
      },

      // Sign out
      signOut: async () => {
        if (!supabase) return
        await supabase.auth.signOut()
        set({
          user: null,
          session: null,
          isAuthenticated: false,
          tier: TIERS.FREE,
          subscription: null,
          tierLimits: TIER_LIMITS[TIERS.FREE],
        })
      },

      // Password reset
      resetPassword: async (email) => {
        if (!supabase) throw new Error('Auth not configured')
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/auth/reset-password`
        })
        if (error) throw error
      },

      // Check feature access
      hasAccess: (feature) => {
        const limits = get().tierLimits
        switch (feature) {
          case 'middles': return limits.middlesAccess
          case 'ev': return limits.evAccess
          case 'props': return limits.propsAccess
          case 'models': return limits.modelAccess
          case 'api': return limits.apiAccess
          default: return true
        }
      },

      // Check arb percentage access
      canViewArb: (arbPercent) => {
        return arbPercent <= get().tierLimits.arbMaxPercent
      },

      // Get refresh rate
      getRefreshRate: () => {
        return get().tierLimits.refreshRate * 1000
      },
    }),
    {
      name: 'abet-auth',
      partialize: (state) => ({
        tier: state.tier,
        emailPreferences: state.emailPreferences,
      }),
    }
  )
)
